import React, { useState, useRef, useCallback, useEffect } from 'react'
import { FaSearch } from 'react-icons/fa'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material'
import './History.css'
import { CircularProgress, Pagination } from '@mui/material'
import { deleteDraft } from '../../services/Draft'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import {
  cleanupPopupStateKeys,
  makePopupStateKey
} from '../../utils/niwariPopupStorage'
import {
  acquirePopupLease,
  releasePopupLease,
  requestPopupFocus
} from '../../utils/niwariPopupCoordinator'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import { useDraftsPaginated } from '../../hooks/History/useGetDrafts'
import { useDebounce } from '../../utils/debounce'

const formatDatePlusOne = (dateString) => {
  if (!dateString) return ''
  const normalizedString = dateString.replace(/\//g, '-')
  const datePart = normalizedString.slice(0, 10)
  const parts = datePart.split('-')
  if (parts.length !== 3) return ''

  const year = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10) - 1
  const day = parseInt(parts[2], 10)

  const date = new Date(year, month, day)
  date.setDate(date.getDate() + 1)

  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}/${mm}/${dd}`
}

const displayDraftName = (name, createdDate) => {
  if (!name) return ''
  const dateStr = formatDatePlusOne(createdDate)
  if (!dateStr) return name
  const last10 = name.slice(-10)
  if (/[\/\-_]/.test(last10)) {
    return name.slice(0, -10) + dateStr
  }
  return name
}

const ITEMS_PER_PAGE = 10

const mappingStatusLabel = {
  draft: '下書き',
  confirmed: '確定'
}

const History = ({ handleAllocatedSchedule, handleRevertSchedule, t }) => {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedId, setSelectedId] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const popupWindowsRef = useRef(new Map())
  const popupOpeningRef = useRef(false)
  const lastPopupOpenedAtRef = useRef(0)
  const [selectedDate, setSelectedDate] = useState(null)

  const closeOwnedPopups = useCallback(() => {
    popupWindowsRef.current.forEach((popup, draftId) => {
      if (popup && !popup.closed) popup.close()
      popupWindowsRef.current.delete(draftId)
    })
  }, [])

  useEffect(() => () => closeOwnedPopups(), [closeOwnedPopups])

  const debouncedSearch = useDebounce(search, 300)

  const {
    data,
    isLoading: isFetching,
    refetch
  } = useDraftsPaginated(
    currentPage,
    selectedDate,
    debouncedSearch,
    statusFilter
  )

  const scheduleData = {
    items: data?.items ?? [],
    total: data?.total ?? 0
  }

  const isLoading = isFetching || actionLoading

  /* =====================
     PAGINATION
  ===================== */
  const totalPages = Math.ceil(scheduleData.total / ITEMS_PER_PAGE)

  /* =====================
     HANDLERS
  ===================== */
  const handleSearchChange = (e) => {
    setSearch(e.target.value)
    setCurrentPage(1)
  }

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value)
    setCurrentPage(1)
  }

  const withLoading = async (action) => {
    try {
      setActionLoading(true)
      await action()
    } catch (error) {
      console.error(error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteDraftItem = (id) =>
    withLoading(async () => {
      setSelectedId(null)
      await deleteDraft(id)
      if (currentPage === 1) {
        refetch()
      } else {
        setCurrentPage(1)
      }
    })

  const handleEditDraftItem = (id, readOnly = false) => {
    const now = Date.now()
    if (
      popupOpeningRef.current ||
      now - lastPopupOpenedAtRef.current < 300
    ) {
      return
    }
    popupOpeningRef.current = true
    lastPopupOpenedAtRef.current = now
    let popupLease

    try {
      popupLease = acquirePopupLease(id)
      if (!popupLease.acquired) {
        requestPopupFocus(id)
        return
      }

      const openInstanceKey = Date.now()
      const stateKey = makePopupStateKey(id, openInstanceKey)
      cleanupPopupStateKeys([stateKey])

      const params = new URLSearchParams({
        draftId: id.toString(),
        openedAt: openInstanceKey.toString(),
        stateKey,
        popupOwnerId: popupLease.ownerId
      })
      if (readOnly) params.set('readOnly', '1')

      const url = `${window.location.origin}/allocation/schedule/right-panel?${params.toString()}`

      const screenHeight = window.screen.height
      const popupHeight = Math.floor(screenHeight * 0.8)
      const screenWidth = window.screen.width
      const popupWidth = Math.floor(screenWidth * 0.9)

      const popup = window.open(
        url,
        `niwari_${id}`,
        `width=${popupWidth},height=${popupHeight},scrollbars=yes,resizable=yes,status=yes`
      )

      if (popup) {
        popupWindowsRef.current.set(String(id), popup)
        popup.focus()
      } else {
        releasePopupLease(id, popupLease.ownerId)
      }
    } catch (error) {
      if (popupLease?.acquired) {
        releasePopupLease(id, popupLease.ownerId)
      }
      console.error(error)
    } finally {
      popupOpeningRef.current = false
    }
  }

  const handleOpenDelete = (id) => {
    setSelectedId(id)
  }

  const handleClose = () => {
    setSelectedId(null)
  }

  /* =====================
     RENDER
  ===================== */
  return (
    <div className='so-page'>
      {/* ===== HEADER ===== */}
      <div className='so-header'>
        <div className='so-header-right'>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ja'>
            <DatePicker
              slotProps={{ field: { clearable: true } }}
              value={selectedDate ? dayjs(selectedDate) : null}
              onChange={(newValue) => {
                setCurrentPage(1)
                setSelectedDate(newValue?.format('YYYY-MM-DD'))
              }}
              format='YYYY年M月D日'
            />
          </LocalizationProvider>

          <div className='so-tool so-search'>
            <FaSearch />
            <input
              placeholder='セリ人名から検索する'
              value={search}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      {/* ===== TABLE ===== */}
      <div className='so-table-wrapper-box'>
        <div className='so-table-wrapper'>
          <table className='so-table relative'>
            <thead>
              <tr className='so-filter-row'>
                <th>
                  <input
                    className='center'
                    placeholder='荷割結果一覧'
                    disabled
                  />
                </th>
                <th>
                  <input className='center' placeholder='作成日' disabled />
                </th>
                <th>
                  <select
                    className='center'
                    value={statusFilter}
                    onChange={handleStatusChange}
                  >
                    <option value=''>すべてのステータス</option>
                    <option value='draft'>下書き</option>
                    <option value='confirmed'>確定</option>
                  </select>
                </th>
                <th className='center text-xl'>操作</th>
              </tr>
            </thead>

            <tbody>
              {scheduleData.items.map((s) => (
                <tr key={s.id}>
                  <td className='primary'>
                    {displayDraftName(s.name, s.date)}
                  </td>
                  <td>{new Date(s.date).toLocaleDateString('ja-JP')}</td>
                  <td>
                    <span className={`so-status ${s.confirm_state}`}>
                      {mappingStatusLabel[s.confirm_state]}
                    </span>
                  </td>
                  <td className='center'>
                    {s.confirm_state === 'draft' ? (
                      <div className='flex justify-center items-center gap-3'>
                        <EditIcon
                          className='so-action'
                          fontSize='14'
                          title='編集'
                          onClick={() =>
                            handleEditDraftItem(
                              s.id,
                              s.confirm_state === 'confirmed'
                            )
                          }
                        />

                        <DeleteIcon
                          className='so-action'
                          fontSize='14'
                          title='削除'
                          onClick={() => handleOpenDelete(s.id)}
                        />
                      </div>
                    ) : (
                      <div className='flex justify-center'>
                        <VisibilityIcon
                          className='so-action'
                          fontSize='14'
                          title='表示'
                          onClick={() =>
                            handleEditDraftItem(
                              s.id,
                              s.confirm_state === 'confirmed'
                            )
                          }
                        />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              <ConfirmDialog
                open={Boolean(selectedId)}
                title='下書きを削除'
                description='この操作は元に戻せません。続行しますか？'
                confirmText='削除'
                cancelText='キャンセル'
                onClose={handleClose}
                onConfirm={() => {
                  handleDeleteDraftItem(selectedId)
                }}
              />

              {scheduleData.items.length === 0 && (
                <tr>
                  <td colSpan={4} className='so-empty'>
                    データが見つかりません
                  </td>
                </tr>
              )}
            </tbody>

            {isLoading && (
              <div className='absolute top-0 left-0 bg-gray-100 opacity-50 w-full h-full flex justify-center items-center'>
                <CircularProgress color='inherit' />
              </div>
            )}
          </table>

          {/* ===== PAGINATION ===== */}
          {totalPages > 1 && (
            <div className='so-pagination'>
              <span className='text-2xl'>
                {scheduleData.total}件中{' '}
                {(currentPage - 1) * ITEMS_PER_PAGE + 1}～
                {Math.min(currentPage * ITEMS_PER_PAGE, scheduleData.total)}
              </span>

              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                shape='rounded'
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '24px',
                    padding: '2px'
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default History
